import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import Breadcrumbs, { BreadcrumbItem } from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import ListActionsMenu, {
	ListToolbarAction,
} from "../../components/Shared/ListPageToolbar/ListActionsMenu";
import { InternalUsersBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import { enable2FARequest, UpdateUserRequest, verify2FARequest } from "../../models/InternalUser";
import {
	useConsultantCountByManager,
	useDeleteInternalUser,
	useEnable2FA,
	useInternalUser,
	useUpdateInternalUser,
	useVerify2FA,
} from "../../networking/internal-user.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { ConfirmModalProps } from "../../stores/confirmModal.store";
import { formatRoute } from "../../utils/route.utils";
import UserForm from "./UserForm";
import Modal from "../../components/Core/Modal/Modal";
import Flex from "../../components/Layout/Flex";
import AppText from "../../components/Core/AppText/AppText";
import { useModalStore } from "../../hooks/modal.hooks";
import Button from "../../components/Core/Button/Button";
import { motion } from "framer-motion";
import OtpInput from "react-otp-input";
import AppIcon from "../../components/Core/AppIcon/AppIcon";
import { FEEDBACK_MESSAGE_TYPE } from "../../configs/feedbackMessages";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import TransferConsultantsModal from "../../components/Users/TransferConsultantsModal";
import { toast } from "../../utils/toast";
import { useAuthStore } from "../../stores/auth.store";
import { useConfirmModalStore } from "../../stores/confirmModal.store";

interface UserParamProps {
	userId: string;
}

const UsersProfile: React.FC = () => {
	const modalStore = useConfirmModalStore();
	const authStore = useAuthStore();
	const { t } = useTranslation();
	const toggleModal = useModalStore((state) => state.toggleMessageModal);
	const [step, setStep] = useState<number>(-1);
	const [otp, setOtp] = useState("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [otpError, setOtpError] = useState<boolean>(false);
	const [otpErrorMessage, setOtpErrorMessage] = useState<string>("");
	const [verificationId, setVerificationId] = useState("");
	const [otpEnabled, setOtpEnabled] = useState<boolean>(false);
	const [timer, setTimer] = useState(60); // 60 seconds countdown
	const [isDisabled, setIsDisabled] = useState(false);
	const [showTransferModal, setShowTransferModal] = useState(false);
	const [consultantCount, setConsultantCount] = useState(0);

	const { data: organisation } = useOrganisation();

	const { userId } = useParams<keyof UserParamProps>() as UserParamProps;

	const { user } = useInternalUser(userId);
	const { mutateAsync: updateInternalUser } = useUpdateInternalUser();
	const { mutateAsync: deleteInternalUser } = useDeleteInternalUser();
	const { mutateAsync: getConsultantCountByManager } = useConsultantCountByManager();
	const { mutateAsync: enable2FA, isLoading: isEnabling2FA } = useEnable2FA();
	const { mutateAsync: verify2FA, isLoading: isVerifying2FA } = useVerify2FA();

	const navigate = useNavigate();

	const actions: ListToolbarAction[] = [
		{
			label: user?.active ? "DEACTIVATE" : "ACTIVATE",
			onClick: () => changeActivationConfirm(),
		},
		{
			label: "consultant.invite",
			onClick: () => inviteUserConfirm(),
		},
		{
			label: "TRANSFER_CONSULTANTS",
			onClick: () => openTransferConsultantsModal(),
		},
		{
			label: "DELETE_USER",
			danger: true,
			dividerBefore: true,
			onClick: () => deleteUserConfirm(),
		},
	];

	const updateUserActiveStatus = async () => {
		const updateUserActiveStatus: UpdateUserRequest = {
			active: user?.active ? false : true,
		};
		const result = await updateInternalUser({ userId, update: updateUserActiveStatus })
			.then(() => true)
			.catch(() => false);

		if (result) {
			toast.successGeneric();
			navigate(formatRoute(routes.INTERNAL_USERS));
		} else {
			toast.errorGeneric();
		}
	};

	const deleteUserConfirm = () => {
		const deleteConfirmModal: ConfirmModalProps = {
			title: "DELETE_USER",
			text: "DELETE_USER_CONFIRM",
			acceptButtonText: "YES",
			rejectButtonText: "NO",
			onAccept: () => deleteUser(),
			onReject: () => {},
			type: "DANGER",
		};
		modalStore.newModal(deleteConfirmModal);
	};

	const inviteUserConfirm = () => {
		const inviteModal: ConfirmModalProps = {
			title: t("inviteModal.title", { name: user?.firstName }),
			text: t("inviteModal.text", { email: user?.email }),
			acceptButtonText: "SEND",
			rejectButtonText: "CANCEL",
			onAccept: () => inviteConsultant(),
			onReject: () => {},
			type: "SUCCESS",
		};
		modalStore.newModal(inviteModal);
	};

	const changeActivationConfirm = () => {
		const type = user?.active ? "DANGER" : "SUCCESS";
		const changeActivationModal: ConfirmModalProps = {
			title: `${user?.active ? "DEACTIVATE_USER_TITLE" : "ACTIVATE_USER_TITLE"}`,
			text: `${user?.active ? "DEACTIVATE_USER_TEXT" : "ACTIVATE_USER_TEXT"}`,
			acceptButtonText: "YES",
			rejectButtonText: "NO",
			onAccept: () => updateUserActiveStatus(),
			onReject: () => {},
			type,
		};
		modalStore.newModal(changeActivationModal);
	};

	const inviteConsultant = async () => {
		if (user?.email) {
			const success = await authStore.forgotPassword(user.email, organisation?.id);

			if (success) {
				toast.success(t("inviteModal.success"));
			} else {
				toast.success(t("inviteModal.failure"));
			}
		}
	};

	const deleteUser = async () => {
		const result = await deleteInternalUser(userId)
			.then(() => true)
			.catch(() => false);

		if (result) {
			navigate(routes.INTERNAL_USERS);
		}
	};

	const openTransferConsultantsModal = async () => {
		if (!organisation?.id) {
			toast.error("Organisation not found");
			return;
		}

		try {
			// Fetch consultant count for this manager
			const count = await getConsultantCountByManager(userId);
			setConsultantCount(count);
			setShowTransferModal(true);
		} catch (error) {
			toast.error("Failed to fetch consultant count");
		}
	};

	const handleTransferSuccess = () => {
		// Refresh the page or navigate as needed
		toast.successGeneric();
	};

	useEffect(() => {
		if (user && user?.twoFactor && user?.twoFactor === "sms") {
			setOtpEnabled(true);
		} else {
			setOtpEnabled(false);
		}
	}, [user]);

	const navigateToUserDetails = () => {
		return formatRoute(routes.INTERNAL_USER_PROFILE, [userId]);
	};
	const InternalUsersDetailsBreadcrumbs: BreadcrumbItem[] = [
		...InternalUsersBreadcrumbs,
		{
			title: `${user?.firstName || ""} ${" "} ${user?.lastName || ""}`,
			path: navigateToUserDetails(),
		},
	];

	const handleSubmit = async () => {
		if (step !== 2) {
			setStep(step + 1);
		} else {
			setStep(-1);
			closeModal();
		}
	};

	function closeModal() {
		toggleModal();
		setStep(-1);
		setOtp("");
		setOtpError(false);
		setOtpErrorMessage("");
		setPhoneNumber("");
		setTimer(60);
		setVerificationId("");
		refreshPage();
	}

	async function validate2FA() {
		if (otp && verificationId) {
			setOtpError(false);
			const verify2FARequest: verify2FARequest = {
				code: otp,
				verificationId: verificationId,
			};
			const otpVerifyResult = await verify2FA(verify2FARequest)
				.then(() => true)
				.catch((e) => e);

			if (otpVerifyResult === true) {
				handleSubmit();
				toast.success(FEEDBACK_MESSAGE_TYPE.CHANGES_SAVE_SUCCESSFULLY);
			} else {
				setOtpError(true);
				setOtpErrorMessage(t("otp2FA.otpErrorMessage"));
			}
		} else {
			if (!otp) {
				setOtpError(true);
				setOtpErrorMessage(t("otp2FA.otpEmptyErrorMessage"));
			}
			toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
		}
	}
	async function reSendOtpSms() {
		if (phoneNumber) {
			const enable2FARequest: enable2FARequest = {
				phone: phoneNumber,
				user: {
					id: userId,
				},
			};
			const otpResult: any = await enable2FA(enable2FARequest).catch((e) => e);
			if (otpResult.verificationId) {
				toast.success(FEEDBACK_MESSAGE_TYPE.RESEND_SUCCESSFUL);
				setVerificationId(otpResult.verificationId);
				if (verificationId) {
					setTimer(otpResult?.ttl || 60);
					setIsDisabled(true);
				}
			} else {
				toast.error(FEEDBACK_MESSAGE_TYPE.RESEND_FAILED);
			}
		}
	}

	useEffect(() => {
		let interval: any;
		if (isDisabled) {
			interval = setInterval(() => {
				setTimer((prev) => {
					if (prev === 1) {
						clearInterval(interval);
						setIsDisabled(false);
						return 0;
					}
					return prev - 1;
				});
			}, 1000);
		}
		return () => clearInterval(interval);
	}, [isDisabled]);

	function verificationIdHandler(verificationId: string) {
		setVerificationId(verificationId);
		if (verificationId) {
			setIsDisabled(true);
		}
	}

	const refreshPage = () => {
		window.location.reload();
	};

	return (
		<>
			<Breadcrumbs items={InternalUsersDetailsBreadcrumbs} />
			<div className="p-4 px-6">
				<div className="flex justify-end">
					<ListActionsMenu actions={actions} />
				</div>

				<UserForm
					isEdit={true}
					nextStep={() => handleSubmit()}
					setOtpEnabled={setOtpEnabled}
					setVerificationId={verificationIdHandler}
					setPhoneNumber={setPhoneNumber}
					otpEnabled={otpEnabled}
					setTimer={setTimer}
					refreshPage={refreshPage}
					otpStatus={user?.twoFactor === "sms"}
				/>
				{otpEnabled && step > -1 && (
					<Modal onClose={closeModal}>
						{(isEnabling2FA || isVerifying2FA) && <LoadingIndicator showText={false} />}
						{step === 0 && (
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -20 }}
								transition={{ duration: 0.5 }}
							>
								<Flex direction="column">
									<Flex
										justifyContent="center"
										alignItems="center"
										direction="row"
										margin="0 0 1rem 0"
									>
										<AppText size="l" weight="medium" margin="0 0 1rem 0" textAlign="center">
											{t("otp2FA.enable2FA")}
										</AppText>
									</Flex>

									<AppText size="m" weight="light" margin="0 0 1rem 0" textAlign="center">
										{t("otp2FA.enable2FAMessage")}
									</AppText>

									<Flex
										direction="column"
										alignItems="center"
										justifyContent="center"
										margin="1.5rem 0 0 0"
										gap="0.75rem"
									>
										<Button onClick={handleSubmit} width="80%">
											{t("otp2FA.sendCode")}
										</Button>
										<Button
											width="80%"
											loading={false}
											onClick={() => closeModal()}
											textColor="black"
											backgroundColor="buttonGray"
										>
											{t("otp2FA.cancel")}
										</Button>
									</Flex>
								</Flex>
							</motion.div>
						)}
						{step === 1 && (
							<motion.div
								initial={{ opacity: 0, x: 100 }} // Start from right (100px)
								animate={{ opacity: 1, x: 0 }} // Move to original position
								exit={{ opacity: 0, x: -100 }} // Exit to the left (-100px)
								transition={{ duration: 0.5, ease: "easeOut" }}
							>
								<Flex direction="column">
									<Flex
										justifyContent="center"
										alignItems="center"
										direction="row"
										margin="0 0 1rem 0"
									>
										<AppText size="l" weight="medium" margin="0 0 1rem 0" textAlign="center">
											{t("otp2FA.enterCode")}
										</AppText>
									</Flex>
									<AppText size="m" weight="light" margin="0 0 1rem 0" textAlign="center">
										{t("otp2FA.enable2FAMessage")}
									</AppText>
									<Flex direction="row" justifyContent="center" alignItems="center">
										<OtpInput
											value={otp}
											onChange={setOtp}
											numInputs={6}
											renderSeparator={<span> </span>}
											renderInput={(props) => <input {...props} />}
											inputStyle={{
												width: "50px",
												height: "50px",
												margin: "0 5px",
												fontSize: "20px",
												borderRadius: "4px",
												border: "1px solid gray",
												textAlign: "center",
												outline: "none",
											}}
										/>
									</Flex>
									{otpError && (
										<Flex
											direction="row"
											justifyContent="center"
											alignItems="center"
											margin="1.5rem 0 0 0"
										>
											<p style={{ fontSize: "12px", color: "red" }}>{otpErrorMessage}</p>
										</Flex>
									)}

									<Flex
										direction="column"
										alignItems="center"
										justifyContent="center"
										margin="1.5rem 0 0 0"
										gap="0.75rem"
									>
										<Button onClick={() => validate2FA()} width="80%">
											{t("otp2FA.confirm")}
										</Button>
										<Button
											width="80%"
											loading={false}
											onClick={() => closeModal()}
											textColor="black"
											backgroundColor="buttonGray"
										>
											{t("otp2FA.cancel")}
										</Button>
									</Flex>
									<Flex
										direction="row"
										justifyContent="center"
										alignItems="center"
										margin="1.5rem 0 0 0"
									>
										{isDisabled ? (
											<p style={{ fontSize: "12px", color: "#000" }}>
												{t("otp2FA.resendCodeMessage")}
												{` `} {timer} {` `} {t("otp2FA.second")}
											</p>
										) : (
											<p style={{ fontSize: "12px", color: "#000" }}>
												{t("otp2FA.resendCodeRemaining")}{" "}
												<a
													href="#"
													onClick={(e) => {
														e.preventDefault();
														reSendOtpSms();
													}}
													style={{
														color: "#3498db",
														textDecoration: "underline",
														cursor: "pointer",
													}}
												>
													{t("otp2FA.sendNewCode")}
												</a>
											</p>
										)}
									</Flex>
								</Flex>
							</motion.div>
						)}
						{step === 2 && (
							<motion.div
								initial={{ opacity: 0, x: 100 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: -100 }}
								transition={{ duration: 0.5, ease: "easeOut" }}
							>
								<Flex direction="column">
									<Flex
										justifyContent="center"
										alignItems="center"
										direction="row"
										margin="0 0 1rem 0"
									>
										<AppIcon icon="otp_check" size="75px" />
									</Flex>

									<Flex
										justifyContent="center"
										alignItems="center"
										direction="row"
										margin="0 0 1rem 0"
									>
										<AppText size="l" weight="medium" margin="0 0 1rem 0" textAlign="center">
											{t("otp2FA.enabled2FA")}
										</AppText>
									</Flex>

									<AppText size="m" weight="light" margin="0 0 1rem 0" textAlign="center">
										{t("otp2FA.enabled2FAMessage")}
									</AppText>

									<Flex
										direction="column"
										alignItems="center"
										justifyContent="center"
										margin="1.5rem 0 0 0"
										gap="0.75rem"
									>
										<Button onClick={() => closeModal()} width="80%">
											{t("otp2FA.close")}
										</Button>
									</Flex>
								</Flex>
							</motion.div>
						)}
					</Modal>
				)}
				{showTransferModal && organisation && (
					<TransferConsultantsModal
						sourceManagerId={userId}
						sourceManagerName={`${user?.firstName || ""} ${user?.lastName || ""}`}
						organisationId={organisation.id}
						consultantCount={consultantCount}
						onClose={() => setShowTransferModal(false)}
						onSuccess={handleTransferSuccess}
					/>
				)}
			</div>
		</>
	);
};

export default UsersProfile;
